import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCrmTenantsQuery, useSyncCrmTenantsMutation } from '../../../../../store/api';
import { RowFieldErrors, TenantRow, TenantRowField } from '../types';
import {
  areRowsEqualToTenants,
  createEmptyRow,
  getRowFieldErrors,
  rowsToTenants,
  tenantsToRows,
  validateRows
} from '../utils';

const syncFieldErrors = (
  nextRows: TenantRow[],
  prevErrors: Record<string, RowFieldErrors>
): Record<string, RowFieldErrors> =>
  Object.keys(prevErrors).length ? getRowFieldErrors(nextRows) : prevErrors;

export const useCrmTenantsEditor = () => {
  const { t } = useTranslation();
  const { data, isLoading, isFetching } = useGetCrmTenantsQuery();
  const [syncCrmTenants, { isLoading: isSaving }] = useSyncCrmTenantsMutation();

  const [rows, setRows] = useState<TenantRow[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [rowFieldErrors, setRowFieldErrors] = useState<Record<string, RowFieldErrors>>({});
  const [saveError, setSaveError] = useState(false);

  const serverTenants = data?.data ?? {};
  const showLoading = isLoading || isFetching || isSaving;
  const hasRows = rows.length > 0;

  const currentTenants = useMemo(() => rowsToTenants(rows), [rows]);
  const hasChanges = useMemo(
    () => !areRowsEqualToTenants(rows, serverTenants),
    [rows, serverTenants]
  );

  const resetFormState = useCallback(
    (tenants = serverTenants) => {
      setRows(tenantsToRows(tenants));
      setValidationError(null);
      setRowFieldErrors({});
    },
    [serverTenants]
  );

  const handleChangeRow = useCallback((id: string, field: TenantRowField, value: string) => {
    setRows((prev) => {
      const next = prev.map((row) => (row.id === id ? { ...row, [field]: value } : row));

      setRowFieldErrors((prevErrors) => syncFieldErrors(next, prevErrors));

      return next;
    });
    setValidationError(null);
  }, []);

  const handleDeleteRow = useCallback((id: string) => {
    setRows((prev) => {
      const next = prev.filter((row) => row.id !== id);

      setRowFieldErrors((prevErrors) => syncFieldErrors(next, prevErrors));

      return next;
    });
    setValidationError(null);
  }, []);

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, createEmptyRow()]);
  }, []);

  const handleCancel = useCallback(() => {
    resetFormState();
  }, [resetFormState]);

  const handleSave = useCallback(async () => {
    const fieldErrors = getRowFieldErrors(rows);
    const error = validateRows(rows);

    if (error) {
      setRowFieldErrors(fieldErrors);
      setValidationError(t(`settingsPage.crmTenants.errors.${error}`));
      return;
    }

    setRowFieldErrors({});

    try {
      await syncCrmTenants({ tenants: currentTenants }).unwrap();
      setValidationError(null);
    } catch {
      setSaveError(true);
    }
  }, [currentTenants, rows, syncCrmTenants, t]);

  const handleCloseSaveError = useCallback(() => {
    setSaveError(false);
  }, []);

  useEffect(() => {
    if (data?.data) {
      resetFormState(data.data);
    }
  }, [data?.data, resetFormState]);

  return {
    rows,
    rowFieldErrors,
    validationError,
    saveError,
    showLoading,
    hasRows,
    hasChanges,
    isLoading,
    handleChangeRow,
    handleDeleteRow,
    handleAddRow,
    handleCancel,
    handleSave,
    handleCloseSaveError
  };
};
